import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Copy, ArrowLeft, Bot, Swords, Crown } from "lucide-react";
import api from "../lib/api";
import Navbar from "../components/Navbar";

/**
 * Replay — public spectator page for a tournament bracket.
 *
 * Mounted at `/replay/:id`. Fetches the bracket via the public
 * `/api/tournaments/{id}/replay` endpoint (no auth required) so a
 * shared link works for logged-out friends. Surfaces:
 *  - Header with level + winner banner (when complete)
 *  - Side-by-side match scores per round
 *  - Shared bracket_seed + "Copy Replay Link" CTA
 *  - "Play This Replay" launches /play/<level>?replay_seed=<seed>
 */
export default function Replay() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/tournaments/${id}/replay`)
      .then((r) => {
        if (!cancelled) setT(r.data);
      })
      .catch((e) => {
        if (!cancelled) {
          setErr(e?.response?.status === 404 ? "Bracket not found" : "Couldn't load replay");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.success(label, { description: text });
    }
  };

  if (err) {
    return (
      <div className="relative min-h-screen">
        <div className="starfield" />
        <Navbar />
        <main className="relative z-10 max-w-3xl mx-auto px-4 pt-24 pb-16">
          <Link
            to="/tournaments"
            className="inline-flex items-center gap-1.5 text-white/55 hover:text-white font-mono-cosmic text-[10px] tracking-[0.3em] uppercase mb-4"
          >
            <ArrowLeft className="w-3 h-3" /> Brackets
          </Link>
          <div
            className="border border-[#ff007f]/50 bg-[#ff007f]/10 p-6 text-white/80 font-mono-cosmic text-xs tracking-[0.2em] uppercase"
            data-testid="replay-error"
          >
            {err}
          </div>
        </main>
      </div>
    );
  }

  if (!t) {
    return (
      <div className="relative min-h-screen">
        <div className="starfield" />
        <Navbar />
        <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 pt-24 pb-16">
          <div className="text-white/50 font-mono-cosmic text-xs tracking-[0.25em] uppercase">
            Loading…
          </div>
        </main>
      </div>
    );
  }

  const lookup = (uid) =>
    (t.participants || []).find((p) => p.user_id === uid) || null;
  const rounds = {};
  for (const m of t.matches || []) {
    if (!rounds[m.round]) rounds[m.round] = [];
    rounds[m.round].push(m);
  }
  const roundKeys = Object.keys(rounds).map(Number).sort((a, b) => a - b);
  const seed = t.bracket_seed || "";
  const short = seed ? `${seed.slice(0, 6)}…${seed.slice(-4)}` : "—";
  const replayUrl = `${window.location.origin}/play/${t.level}?replay_seed=${seed}`;
  // OG-rich share URL — points at the FastAPI share wrapper so Discord
  // / Twitter / Slack render a beautiful 1200x630 SVG preview card with
  // the bracket scoreline before the browser bounces into the SPA.
  const shortUrl = `${window.location.origin}/api/share/replay/${t.id}`;
  const winner = t.winner_id ? lookup(t.winner_id) : null;

  return (
    <div className="relative min-h-screen">
      <div className="starfield" />
      <Navbar />
      <main
        className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 pt-24 pb-16 fade-in"
        data-testid="replay-page"
      >
        <Link
          to="/tournaments"
          className="inline-flex items-center gap-1.5 text-white/55 hover:text-white font-mono-cosmic text-[10px] tracking-[0.3em] uppercase mb-4"
        >
          <ArrowLeft className="w-3 h-3" /> Brackets
        </Link>

        {/* Header */}
        <div className="mb-6">
          <div className="font-mono-cosmic text-[10px] tracking-[0.4em] text-[#b026ff]/70 uppercase mb-2">
            // REPLAY · {t.id}
          </div>
          <h1
            className="font-heading text-3xl sm:text-4xl tracking-tight text-white"
            data-testid="replay-title"
          >
            Level {t.level} · {t.size}v{t.size === 2 ? "1" : t.size}
          </h1>
          <div className="flex flex-wrap gap-3 mt-2 font-mono-cosmic text-[10px] tracking-[0.2em] uppercase text-white/55">
            <span>Status: {t.status}</span>
            <span>·</span>
            <span>{t.buyin} ☾ buy-in</span>
            <span>·</span>
            <span>Pot {t.pot} ☾</span>
            {winner && (
              <>
                <span>·</span>
                <span
                  className="text-[#ffd700] flex items-center gap-1"
                  data-testid="replay-winner-chip"
                >
                  <Crown className="w-3 h-3" /> {winner.name || "Unknown"} won
                </span>
              </>
            )}
          </div>
        </div>

        {/* Seed share panel */}
        <div
          className="mb-8 border border-[#b026ff]/40 bg-[#b026ff]/5 p-4"
          data-testid="replay-seed-panel"
        >
          <div className="flex flex-wrap items-center gap-3">
            <Swords className="w-4 h-4 text-[#b026ff]" />
            <div>
              <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-[#b026ff]/85">
                Shared Replay Seed
              </div>
              <div
                className="font-mono-cosmic text-xs tracking-[0.15em] text-white/85 tabular-nums truncate max-w-xs"
                data-testid="replay-seed-value"
              >
                {short}
              </div>
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => copy(shortUrl, "Replay URL")}
                className="px-3 py-1.5 border border-white/15 hover:border-[#b026ff] hover:text-[#b026ff] text-white/65 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase transition inline-flex items-center gap-1.5"
                data-testid="replay-copy-short-btn"
              >
                <Copy className="w-3 h-3" /> Replay URL
              </button>
              <button
                type="button"
                onClick={() => copy(replayUrl, "Play link")}
                className="px-3 py-1.5 border border-white/15 hover:border-[#b026ff] hover:text-[#b026ff] text-white/65 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase transition inline-flex items-center gap-1.5"
                data-testid="replay-copy-play-link-btn"
              >
                <Copy className="w-3 h-3" /> Play Link
              </button>
              <Link
                to={`/play/${t.level}?replay_seed=${seed}`}
                className="px-3 py-1.5 border border-[#b026ff] text-[#b026ff] hover:bg-[#b026ff]/15 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase transition inline-flex items-center gap-1.5"
                data-testid="replay-play-now-btn"
              >
                <Trophy className="w-3 h-3" /> Play This Replay
              </Link>
            </div>
          </div>
          <div className="mt-2 font-mono-cosmic text-[10px] tracking-[0.2em] uppercase text-white/45">
            Casual run — no progression / wager side-effects. Same RNG seed as the bracket.
          </div>
        </div>

        {/* Side-by-side match scores */}
        <div className="mb-8">
          <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/50 mb-3">
            // MATCH SCORES
          </div>
          {roundKeys.length === 0 ? (
            <div
              className="border border-white/10 bg-black/30 p-6 text-white/45 font-mono-cosmic text-[10px] tracking-[0.2em] uppercase"
              data-testid="replay-no-matches"
            >
              Bracket hasn't started yet — no matches to show.
            </div>
          ) : (
            <div className="space-y-4">
              {roundKeys.map((r) => {
                const isFinal = r === Math.max(...roundKeys);
                return (
                  <div key={r} data-testid={`replay-round-${r}`}>
                    <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/45 mb-2">
                      {isFinal ? "FINALS" : `ROUND ${r}`}
                    </div>
                    <div className="space-y-2">
                      {rounds[r].map((m) => {
                        const pa = lookup(m.pa);
                        const pb = lookup(m.pb);
                        const aWin = m.winner && m.winner === m.pa;
                        const bWin = m.winner && m.winner === m.pb;
                        return (
                          <div
                            key={`${r}-${m.slot}`}
                            className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center border border-white/15 bg-black/30 p-3"
                            data-testid={`replay-match-${r}-${m.slot}`}
                          >
                            <PilotCell pilot={pa} score={m.score_a} winner={aWin} align="left" />
                            <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/35">
                              vs
                            </div>
                            <PilotCell pilot={pb} score={m.score_b} winner={bWin} align="right" />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function PilotCell({ pilot, score, winner, align }) {
  if (!pilot) {
    return (
      <div className={align === "right" ? "text-right" : ""}>
        <div className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/30">
          TBD
        </div>
      </div>
    );
  }
  const scoreText = score == null ? "—" : Number(score).toLocaleString();
  return (
    <div
      className={`min-w-0 ${align === "right" ? "text-right" : ""}`}
      data-testid={`replay-pilot-${pilot.user_id}`}
    >
      <div
        className={`font-mono-cosmic text-[10px] tracking-[0.2em] uppercase truncate ${
          winner ? "text-[#ffd700]" : "text-white/80"
        }`}
      >
        {winner && <Crown className="w-3 h-3 inline mr-1 -translate-y-0.5" />}
        {pilot.is_bot && <Bot className="w-3 h-3 inline mr-1 -translate-y-0.5 text-[#ff007f]" />}
        {pilot.name}
      </div>
      <div
        className={`font-heading text-lg tabular-nums leading-tight ${
          winner ? "text-[#ffd700]" : "text-white"
        }`}
      >
        {scoreText}
      </div>
    </div>
  );
}
